import React, { useState } from "react";
import { Logo } from "@/components/Logo";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Sparkles, Star, Users, Building2, Mail, Lock, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/format";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const Login = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const initialRole = params.get("role") === "klant" ? "klant" : "kantoor";
  const [role, setRole] = useState(initialRole);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    sessionStorage.setItem("auth_role", role);
    const redirectPath = role === "klant" ? "/mijn-matches" : "/dashboard";
    const redirectUrl = window.location.origin + redirectPath;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      setUser(res.data.user);
      const pending = sessionStorage.getItem("pending_klant_id");
      if (pending && res.data.user.role === "klant") {
        sessionStorage.removeItem("pending_klant_id");
        try { await api.post(`/klanten/me/claim/${pending}`); } catch {}
      }
      navigate(res.data.user.role === "klant" ? "/mijn-matches" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  const isKlant = role === "klant";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between bg-[#1A2B4A] text-white p-12 relative overflow-hidden">
        <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#F5A623]/15 blur-3xl" />
        <div className="relative">
          <Link to="/" className="inline-flex bg-white rounded-xl px-3 py-2" data-testid="login-logo-link">
            <Logo size="md" />
          </Link>
          <h1 className="font-heading text-4xl font-extrabold mt-12 leading-tight tracking-tight">
            {isKlant ? t("login.welcome_klant") : t("login.welcome_kantoor")}
          </h1>
          <p className="text-slate-300 mt-4 text-lg">
            {isKlant ? t("login.subtitle_klant") : t("login.subtitle_kantoor")}
          </p>
        </div>
        <div className="relative space-y-4 text-sm">
          {(isKlant
            ? [
                { icon: Sparkles, t: t("login.benefits_klant_1") },
                { icon: ShieldCheck, t: t("login.benefits_klant_2") },
                { icon: Star, t: t("login.benefits_klant_3") },
              ]
            : [
                { icon: Sparkles, t: t("login.benefits_kantoor_1") },
                { icon: ShieldCheck, t: t("login.benefits_kantoor_2") },
                { icon: Star, t: t("login.benefits_kantoor_3") },
              ]
          ).map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center"><Icon size={18} className="text-[#F5A623]" /></div>
                <span className="text-slate-200">{b.t}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" data-testid="login-logo-link-mobile"><Logo size="md" /></Link>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-[#1A2B4A]">{t("login.title")}</h2>
          <p className="text-slate-600 mt-2">{t("login.subtitle")}</p>

          <div className="mt-7 grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-2xl" data-testid="role-tabs">
            <button onClick={() => setRole("klant")}
              className={`py-3 px-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                role === "klant" ? "bg-white shadow-sm text-[#1A2B4A]" : "text-slate-500 hover:text-[#1A2B4A]"
              }`} data-testid="tab-klant">
              <Users size={16} /> {t("login.tab_klant")}
            </button>
            <button onClick={() => setRole("kantoor")}
              className={`py-3 px-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                role === "kantoor" ? "bg-white shadow-sm text-[#1A2B4A]" : "text-slate-500 hover:text-[#1A2B4A]"
              }`} data-testid="tab-kantoor">
              <Building2 size={16} /> {t("login.tab_kantoor")}
            </button>
          </div>

          <form onSubmit={handlePasswordLogin} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-semibold text-[#1A2B4A] uppercase tracking-wider">{t("login.email")}</label>
              <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 focus-within:border-[#F5A623] focus-within:ring-2 focus-within:ring-[#F5A623]/20 transition-colors">
                <Mail size={16} className="text-slate-400" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("login.email_placeholder")} className="w-full bg-transparent outline-none text-sm" data-testid="login-email" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#1A2B4A] uppercase tracking-wider">{t("login.password")}</label>
              <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 focus-within:border-[#F5A623] focus-within:ring-2 focus-within:ring-[#F5A623]/20 transition-colors">
                <Lock size={16} className="text-slate-400" />
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="w-full bg-transparent outline-none text-sm" data-testid="login-password" />
              </div>
            </div>
            {error && (
              <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-3 py-2 text-sm" data-testid="login-error">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <button type="submit" disabled={loading || !email || !password} className="btn-amber w-full !py-3 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="btn-login">
              {loading ? t("common.loading") : t("login.submit")}
            </button>
          </form>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{t("login.or")}</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <button onClick={handleGoogleLogin} type="button" className="mt-6 w-full bg-white border-2 border-slate-200 hover:border-[#1A2B4A] hover:shadow-md transition-all rounded-full px-6 py-3 flex items-center justify-center gap-3 font-semibold text-[#1A2B4A] text-sm" data-testid="google-login-btn">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {t("login.google")}
          </button>

          <div className="mt-8 pt-6 border-t border-slate-100 text-sm text-slate-600 text-center">
            {t("login.no_account")}{" "}
            <Link to={`/aanmelden?role=${role}`} className="font-semibold text-[#F5A623] hover:underline" data-testid="link-register">
              {t("login.create_account")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
